import { useState, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, FileText, Download, FileCheck, BookOpen, Trash2, Sparkles } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { CaseDocument, DocumentStatus } from '../../types';
import { DOCUMENT_STATUS_CONFIG } from '../../types';
import { cn } from '../ui/utils';
import { formatDate, formatFileSize } from '../../utils/formatters';

/**
 * Extract file type from filename.
 */
function getFileType(name: string): string {
  const ext = name.split('.').pop()?.toUpperCase() || 'FILE';
  return ext;
}

export interface EntityProgressInfo {
  detected: number;
  approved: number;
  rejected: number;
}

interface DocumentsPanelProps {
  documents: CaseDocument[];
  selectedDocumentId: string | null;
  onSelectDocument: (documentId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onDownload: (documentId: string, version: 'original' | 'redacted') => void;
  onOpenFileDict?: (documentId: string) => void;
  onDeleteDocument?: (documentId: string) => void;
  entityProgress?: Map<string, EntityProgressInfo>;
  processingDocumentIds?: Set<string>;
}

type StatusFilter = 'all' | DocumentStatus;

interface DocumentTreeNodeProps {
  document: CaseDocument;
  depth: number;
  selectedDocumentId: string | null;
  onSelectDocument: (documentId: string) => void;
  expandedNodes: Set<string>;
  onToggleExpand: (documentId: string) => void;
  formatDate: (dateString: string) => string;
  formatFileSize: (bytes: number) => string;
  onDownload: (documentId: string, version: 'original' | 'redacted') => void;
  onOpenFileDict?: (documentId: string) => void;
  onDeleteDocument?: (documentId: string) => void;
  entityProgress?: Map<string, EntityProgressInfo>;
  processingDocumentIds?: Set<string>;
}

/**
 * Recursive tree node component for displaying documents with children.
 */
function DocumentTreeNode({
  document,
  depth,
  selectedDocumentId,
  onSelectDocument,
  expandedNodes,
  onToggleExpand,
  formatDate,
  formatFileSize,
  onDownload,
  onOpenFileDict,
  onDeleteDocument,
  entityProgress,
  processingDocumentIds,
}: DocumentTreeNodeProps) {
  const hasChildren = document.children && document.children.length > 0;
  const isExpanded = expandedNodes.has(document.id);
  const isSelected = selectedDocumentId === document.id;
  const isContainer = document.is_container;

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand(document.id);
  };

  const handleDocumentClick = () => {
    onSelectDocument(document.id);
    // Also toggle expand/collapse if this is a container
    if (hasChildren) {
      onToggleExpand(document.id);
    }
  };

  const handleDocumentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDocumentClick();
    }
  };

  return (
    <div>
      <div
        onClick={handleDocumentClick}
        onKeyDown={handleDocumentKeyDown}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        className={cn(
          'w-full text-left py-2 hover:bg-gray-50 transition-colors border-l-2',
          'focus:outline-none focus:ring-2 focus:ring-[#7035C4] focus:ring-inset',
          isSelected
            ? 'bg-[#f9f5ff] border-l-[#7035C4]'
            : 'border-l-transparent'
        )}
        style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: '12px' }}
      >
        <div className="flex items-center">
          {/* Expand/collapse chevron */}
          {hasChildren ? (
            <button
              onClick={handleChevronClick}
              className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded transition-colors"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 text-gray-500 transition-transform',
                  !isExpanded && '-rotate-90'
                )}
              />
            </button>
          ) : (
            <div className="w-5 flex-shrink-0" />
          )}

          {/* Document info - consolidated into 2 rows */}
          <div className="flex-1 min-w-0 ml-1">
            <p className="text-sm font-medium truncate" title={document.name}>
              {document.name}
            </p>
            <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 mt-0.5 text-xs text-gray-500">
              <span className="font-medium text-gray-600">
                {getFileType(document.name)}
              </span>
              <span>•</span>
              <span>{formatDate(document.upload_date)}</span>
              <span>•</span>
              <span>{formatFileSize(document.file_size)}</span>
              {document.page_count && !isContainer && (
                <>
                  <span>•</span>
                  <span>{document.page_count} pg</span>
                </>
              )}
              {hasChildren && (
                <>
                  <span>•</span>
                  <span>{document.children!.length} items</span>
                </>
              )}
              <span>•</span>
              <span
                className="flex items-center gap-1"
                style={{ color: DOCUMENT_STATUS_CONFIG[document.status].color }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: DOCUMENT_STATUS_CONFIG[document.status].color }}
                />
                {DOCUMENT_STATUS_CONFIG[document.status].label}
              </span>
            </div>
            {/* Entity progress bar or processing indicator */}
            {(() => {
              const isProcessing = processingDocumentIds?.has(document.id);
              if (isProcessing) {
                return (
                  <div className="mt-1">
                    <div className="flex items-center gap-1 text-[10px] text-[#7035C4] mb-0.5">
                      <Sparkles className="h-3 w-3 animate-pulse" />
                      <span>Analyzing...</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#7035C4] animate-pulse rounded-full" style={{ width: '100%' }} />
                    </div>
                  </div>
                );
              }
              const progress = entityProgress?.get(document.id);
              if (!progress || progress.detected === 0) return null;
              const worked = progress.approved + progress.rejected;
              const pct = Math.round((worked / progress.detected) * 100);
              const approvedPct = (progress.approved / progress.detected) * 100;
              const rejectedPct = (progress.rejected / progress.detected) * 100;
              return (
                <div className="mt-1">
                  <div className="flex items-center justify-between text-[10px] text-gray-500 mb-0.5">
                    <span>{worked}/{progress.detected} entities</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden flex">
                    {approvedPct > 0 && (
                      <div
                        className="h-full bg-green-500 transition-all duration-300"
                        style={{ width: `${approvedPct}%` }}
                      />
                    )}
                    {rejectedPct > 0 && (
                      <div
                        className="h-full bg-red-400 transition-all duration-300"
                        style={{ width: `${rejectedPct}%` }}
                      />
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Download icons + file dict */}
          <div className="flex items-center gap-1 ml-1 flex-shrink-0">
            {!isContainer && onOpenFileDict && (
              <button
                onClick={(e) => { e.stopPropagation(); onOpenFileDict(document.id); }}
                className="p-0.5 hover:bg-gray-200 rounded transition-colors text-gray-400 hover:text-[#7035C4]"
                title="File dictionary"
                aria-label="Open file dictionary"
              >
                <BookOpen className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(document.id, 'original'); }}
              className="p-0.5 hover:bg-gray-200 rounded transition-colors text-gray-400 hover:text-gray-700"
              title="Download original"
              aria-label="Download original"
            >
              <Download className="h-3.5 w-3.5" />
            </button>
            {document.status === 'completed' && (
              <button
                onClick={(e) => { e.stopPropagation(); onDownload(document.id, 'redacted'); }}
                className="p-0.5 hover:bg-gray-200 rounded transition-colors text-gray-400 hover:text-[#7035C4]"
                title="Download redacted"
                aria-label="Download redacted"
              >
                <FileCheck className="h-3.5 w-3.5" />
              </button>
            )}
            {onDeleteDocument && (
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteDocument(document.id); }}
                className="p-0.5 hover:bg-gray-200 rounded transition-colors text-gray-400 hover:text-red-600"
                title="Delete file"
                aria-label="Delete file"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Recursively render children */}
      {hasChildren && isExpanded && (
        <div>
          {document.children!.map((child) => (
            <DocumentTreeNode
              key={child.id}
              document={child}
              depth={depth + 1}
              selectedDocumentId={selectedDocumentId}
              onSelectDocument={onSelectDocument}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              formatDate={formatDate}
              formatFileSize={formatFileSize}
              onDownload={onDownload}
              onOpenFileDict={onOpenFileDict}
              onDeleteDocument={onDeleteDocument}
              entityProgress={entityProgress}
              processingDocumentIds={processingDocumentIds}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Build a tree structure from flat documents list using parent_id.
 * Returns only root documents (those without a parent_id).
 */
function buildDocumentTree(documents: CaseDocument[]): CaseDocument[] {
  // Create a map of document ID to document (with empty children array)
  const docMap = new Map<string, CaseDocument>();
  documents.forEach((doc) => {
    docMap.set(doc.id, { ...doc, children: [] });
  });

  // Build tree by assigning children to parents
  const rootDocs: CaseDocument[] = [];
  documents.forEach((doc) => {
    const docWithChildren = docMap.get(doc.id)!;
    if (doc.parent_id && docMap.has(doc.parent_id)) {
      const parent = docMap.get(doc.parent_id)!;
      parent.children = parent.children || [];
      parent.children.push(docWithChildren);
    } else {
      // No parent or parent not in list - treat as root
      rootDocs.push(docWithChildren);
    }
  });

  return rootDocs;
}

/**
 * Filter document tree, keeping nodes that match filter or have matching descendants.
 * Also keeps ancestors of matching nodes.
 */
function filterDocumentTree(
  documents: CaseDocument[],
  statusFilter: StatusFilter
): CaseDocument[] {
  if (statusFilter === 'all') return documents;

  function filterNode(doc: CaseDocument): CaseDocument | null {
    // Filter children recursively
    const filteredChildren = (doc.children || [])
      .map(filterNode)
      .filter((c): c is CaseDocument => c !== null);

    // Include this node if it matches OR has matching descendants
    if (doc.status === statusFilter || filteredChildren.length > 0) {
      return { ...doc, children: filteredChildren };
    }

    return null;
  }

  return documents.map(filterNode).filter((d): d is CaseDocument => d !== null);
}

/**
 * Count total documents in tree (including nested children).
 */
function countDocuments(documents: CaseDocument[]): number {
  let count = 0;
  function countNode(doc: CaseDocument) {
    count++;
    (doc.children || []).forEach(countNode);
  }
  documents.forEach(countNode);
  return count;
}

export function DocumentsPanel({
  documents,
  selectedDocumentId,
  onSelectDocument,
  isCollapsed,
  onToggleCollapse,
  onDownload,
  onOpenFileDict,
  onDeleteDocument,
  entityProgress,
  processingDocumentIds,
}: DocumentsPanelProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Build tree structure from flat documents
  const documentTree = useMemo(() => buildDocumentTree(documents), [documents]);

  // Apply status filter to tree
  const filteredTree = useMemo(
    () => filterDocumentTree(documentTree, statusFilter),
    [documentTree, statusFilter]
  );

  const totalCount = useMemo(() => countDocuments(documentTree), [documentTree]);

  const onToggleExpand = useCallback((documentId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }, []);

  // Collapsed state - just show toggle button
  if (isCollapsed) {
    return (
      <div className="w-10 border-r border-border bg-gray-50 flex flex-col items-center py-4">
        <button
          onClick={onToggleCollapse}
          className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
          aria-label="Expand documents panel"
        >
          <ChevronRight className="h-5 w-5 text-gray-600" />
        </button>
        <div className="mt-4 flex flex-col items-center gap-2">
          <FileText className="h-5 w-5 text-gray-400" />
          <span className="text-xs text-gray-500 writing-vertical-lr text-center">
            {totalCount} docs
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 border-r border-border bg-white flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-sm">Documents</h3>
          <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
            {totalCount}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onToggleCollapse}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Collapse documents panel"
          >
            <ChevronLeft className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="p-3 border-b border-border">
        <Select
          value={statusFilter}
          onValueChange={(value: string) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger size="sm">
            <SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {(Object.keys(DOCUMENT_STATUS_CONFIG) as DocumentStatus[]).map((status) => (
              <SelectItem key={status} value={status}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: DOCUMENT_STATUS_CONFIG[status].color }}
                  />
                  {DOCUMENT_STATUS_CONFIG[status].label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Document Tree */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {filteredTree.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            {totalCount === 0 ? 'No documents yet' : 'No documents match filter'}
          </div>
        ) : (
          <div className="py-1">
            {filteredTree.map((doc) => (
              <DocumentTreeNode
                key={doc.id}
                document={doc}
                depth={0}
                selectedDocumentId={selectedDocumentId}
                onSelectDocument={onSelectDocument}
                expandedNodes={expandedNodes}
                onToggleExpand={onToggleExpand}
                formatDate={formatDate}
                formatFileSize={formatFileSize}
                onDownload={onDownload}
                onOpenFileDict={onOpenFileDict}
                onDeleteDocument={onDeleteDocument}
                entityProgress={entityProgress}
                processingDocumentIds={processingDocumentIds}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
